import { useContext, useEffect } from 'react';
import useSWRMutation from 'swr/mutation';
import { mutate } from 'swr';
import {
  API_ADD_WIDGET,
  API_UPDATE_WIDGET,
  API_DELETE_WIDGET,
  API_UPDATE_LAYOUT,
  API_GET_LAYOUT
} from '../../api/configurationScreenServices';
import { WidgetBuilderContext } from '../../contexts/WidgetBuilderContext';
import { formatWidgetConfig } from '../../utils/globalUtils';
import {
  addWidgetUtil,
  deleteWidgetUtil,
  updateWidgetUtil,
  findWidgetPosition,
  findNumberOfWidgetsInASection,
  doesSectionContainStandaloneWidget,
  isWidgetInsideARow
} from '../../utils/previewPaneUtils';
import { getWidgetTypeByWidgetId } from '../../utils/widgetUtils';
import {
  bannerSettingsConstants,
  imageGallerySettingsConstants,
  toastMessageConstants
} from '../../constants/globalConstant';
import { widgetTypes } from '../../constants/globalEnums';
import cloneDeep from 'lodash.clonedeep';

// Merge a mutation's API responses into the SWR layout cache so we don't
// refetch the layout + every widget metavalue after each save (matches the
// old Redux thunks, which built state from the POST/PUT responses).
const mergeLayoutCache = (currentData, layoutData, widget) => {
  const base = currentData || {};
  const sections =
    (layoutData?.hasDraftChanges
      ? layoutData?.draftLayout?.sections
      : layoutData?.mobileLayout?.sections) ||
    base.sections ||
    [];
  return {
    ...base,
    ...layoutData,
    sections,
    widgetConfiguration: {
      ...base.widgetConfiguration,
      ...(widget
        ? {
            [widget.widgetId]: formatWidgetConfig(widget, layoutData?.hasDraftChanges)
          }
        : {})
    }
  };
};

export function useWidgetMutations(homepageId, previewPaneData) {
  const layoutKey = `/homepages/${homepageId}/layout`;

  // WidgetBuilder context drives the shared config-panel footer. Null-safe:
  // useWidgetMutations is also used outside the config panel (ActiveWidgetList,
  // WidgetMenu), where there's no footer to update.
  const widgetBuilder = useContext(WidgetBuilderContext);
  const resetWidgetConfiguration = widgetBuilder?.resetWidgetConfiguration;
  const setFooterConfig = widgetBuilder?.setFooterConfig;

  const {
    trigger: addWidget,
    isMutating: isAdding,
    error: addError
  } = useSWRMutation(
    layoutKey,
    async (key, { arg: metaDataPostValue }) => {
      const { sections, selectedSection } = previewPaneData;

      // Validation checks
      if (findNumberOfWidgetsInASection(selectedSection, previewPaneData) === 5) {
        throw new Error('Maximum 5 widgets per section');
      }

      if (doesSectionContainStandaloneWidget(sections[selectedSection])) {
        throw new Error('Section already contains a standalone widget');
      }

      // Add widget via API
      const widgetResponse = await API_ADD_WIDGET(metaDataPostValue);
      if (widgetResponse?.status !== 200) {
        const errorMessage =
          widgetResponse?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      const { widgetTemplateId, widgetId } = widgetResponse.data.widget;
      let widgetType = getWidgetTypeByWidgetId(widgetTemplateId);

      // Clone and update sections
      const clonedPreviewPane = cloneDeep(previewPaneData);
      let previewPane = { sections: [...clonedPreviewPane.sections] };

      const widgetInformation = {
        widgetId,
        type: isWidgetInsideARow(widgetResponse.data.widget) ? 'half' : 'full',
        widgetType
      };

      // Remove demo widgets based on type
      if (widgetType === 'quicklauncher') {
        previewPane = deleteWidgetUtil(clonedPreviewPane, 'demoLauncherView');
      }
      if (widgetType === widgetTypes.BANNER || widgetType === widgetTypes.TILES_BANNER) {
        previewPane = deleteWidgetUtil(clonedPreviewPane, bannerSettingsConstants.DEMO_BANNER);
      }
      if (widgetType === widgetTypes.IMAGE_GALLERY) {
        previewPane = deleteWidgetUtil(
          clonedPreviewPane,
          imageGallerySettingsConstants.DEMO_GALLERY_IMAGE
        );
      }

      // Add widget to sections
      previewPane = addWidgetUtil(widgetInformation, clonedPreviewPane, selectedSection);

      // Update layout
      const layoutResponse = await API_UPDATE_LAYOUT(previewPane.sections, homepageId);
      if (layoutResponse?.status !== 200) {
        const errorMessage =
          layoutResponse?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      return {
        widget: widgetResponse.data,
        layout: layoutResponse.data
      };
    },
    {
      // Build the cache from the responses instead of refetching everything
      populateCache: (result, currentData) =>
        mergeLayoutCache(currentData, result.layout, result.widget?.widget),
      revalidate: false,
      throwOnError: false,
      // Close the config panel only after the add succeeds.
      onSuccess: () => resetWidgetConfiguration?.()
    }
  );

  const {
    trigger: updateWidget,
    isMutating: isUpdating,
    error: updateError
  } = useSWRMutation(
    layoutKey,
    async (key, { arg: metaDataPostValue }) => {
      const updateWidgetResponse = await API_UPDATE_WIDGET(metaDataPostValue);
      const widgetResponse = updateWidgetResponse?.data;

      if (updateWidgetResponse?.status !== 200) {
        const errorMessage = widgetResponse?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      // Update widget layout
      const clonedPreviewPane = cloneDeep(previewPaneData);
      let previewPane = { sections: [...clonedPreviewPane.sections] };
      const type = isWidgetInsideARow(widgetResponse.widget) ? 'half' : 'full';
      const widgetId = widgetResponse.widget.widgetId;
      previewPane = updateWidgetUtil(previewPane, widgetId, type);

      // Update layout
      const layoutResponse = await API_UPDATE_LAYOUT(previewPane.sections, homepageId);
      if (layoutResponse?.status !== 200) {
        const errorMessage =
          layoutResponse?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      return {
        widget: widgetResponse,
        layout: layoutResponse.data
      };
    },
    {
      // Build the cache from the responses instead of refetching everything
      populateCache: (result, currentData) =>
        mergeLayoutCache(currentData, result.layout, result.widget?.widget),
      revalidate: false,
      throwOnError: false,
      onSuccess: (result) => {
        // Prime the per-widget value cache (used by useWidgetValue to
        // prefill the config panel) with the fresh config from the PUT
        // response — otherwise reopening the editor shows the stale value.
        // result.widget.widget is the metavalue-shaped object, same as the
        // GET /metavalue body useWidgetValue formats.
        const widget = result?.widget?.widget;
        if (widget?.widgetId != null) {
          mutate(`widget-value-${widget.widgetId}`, formatWidgetConfig(widget), {
            revalidate: false
          });
        }
        // Close the config panel only after the update succeeds.
        resetWidgetConfiguration?.();
      }
    }
  );

  const {
    trigger: deleteWidget,
    isMutating: isDeleting,
    error: deleteError
  } = useSWRMutation(
    layoutKey,
    async (key, { arg: { widgetId, shouldUpdateLayout = false } }) => {
      const { sections } = previewPaneData;
      const { sectionIndex } = findWidgetPosition(widgetId, previewPaneData);
      const sectionId = sections[sectionIndex].sectionId;

      // Update layout first if dirty
      if (shouldUpdateLayout) {
        await API_UPDATE_LAYOUT(sections, homepageId);
      }

      // Delete widget
      const response = await API_DELETE_WIDGET(homepageId, sectionId, widgetId);
      if (response?.status !== 200) {
        const errorMessage = response?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      // Fetch the fresh layout STRUCTURE only (rows can collapse and
      // hasDraftChanges can flip server-side). Other widgets' metavalues
      // are untouched by a delete, so no need to refetch them all.
      const layoutResponse = await API_GET_LAYOUT(homepageId);

      return { widgetId, layout: layoutResponse?.data };
    },
    {
      populateCache: (result, currentData) => {
        const merged = mergeLayoutCache(currentData, result?.layout);
        const widgetConfiguration = { ...merged.widgetConfiguration };
        delete widgetConfiguration[result?.widgetId];
        return { ...merged, widgetConfiguration };
      },
      revalidate: false,
      throwOnError: false
    }
  );

  // Drive the shared config-panel Save button's loading state. Guarded so the
  // many ActiveWidgetList instances (each calling this hook) don't thrash the
  // context: only write when the value actually changes.
  const isSaving = isAdding || isUpdating;
  useEffect(() => {
    if (!setFooterConfig) return;
    setFooterConfig((prev) =>
      prev.submitLoading === isSaving ? prev : { ...prev, submitLoading: isSaving }
    );
  }, [isSaving, setFooterConfig]);

  return {
    addWidget,
    updateWidget,
    deleteWidget,
    isLoading: isAdding || isUpdating || isDeleting,
    isAdding,
    isUpdating,
    isDeleting,
    errors: {
      addError,
      updateError,
      deleteError
    }
  };
}
